<template>
  <div
    class="offcanvas offcanvas-end"
    id="OffcanvasForm"
    ref="offcanvas"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ $t('Open Shift') }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body"
      v-else-if="!hasPermission"
    >
      <access-denied/>
    </div>
    <div
      class="offcanvas-body p-0"
      v-else
    >
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-12">
            <label
              for="starting_cash"
              class="form-label"
            >
              {{ $t('Starting Cash') }}
              <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="starting_cash"
                style="font-size: 50px;"
                type="number"
                inputmode="numeric"
                v-model.trim="v$.form.starting_cash.$model"
                class="form-control shadow-none text-center"
              >
              <div v-for="error of v$.form.starting_cash.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
          <div class="col-3">
            <button
              type="button"
              class="border-dark-subtle btn-sm btn btn-light w-100"
              @click="() => {form.starting_cash = parseFloat(form.starting_cash || 0) + 5000}"
            >
            +5,000
            </button>
          </div>
          <div class="col-3">
            <button
              type="button"
              class="border-dark-subtle btn-sm btn btn-light w-100"
              @click="() => {form.starting_cash = parseFloat(form.starting_cash || 0) + 2000}"
            >
            +2,000
            </button>
          </div>
          <div class="col-3">
            <button
              type="button"
              class="border-dark-subtle btn-sm btn btn-light w-100"
              @click="() => {form.starting_cash = parseFloat(form.starting_cash || 0) + 1000}"
            >
            +1,000
            </button>
          </div>
          <div class="col-3">
            <button
              type="button"
              class="border-dark-subtle btn-sm btn btn-light w-100"
              @click="() => {form.starting_cash = parseFloat(form.starting_cash || 0) + 500}"
            >
            +500
            </button>
          </div>
          <div class="col-12">
            <label for="startedAt" class="form-label">
              {{ $t('Start Time') }}
              <span class="text-danger">*</span>
            </label>
            <input
              type="time"
              id="startedAt"
              class="form-control shadow-none"
              v-model.trim="v$.form.started_at.$model"
            >
              <div v-for="error of v$.form.started_at.$errors"
                class="d-block invalid-feedback"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
          </div>
          <div class="col-12">
            <label for="sessionNoStart" class="form-label">
              {{ $t('Starting Session No.') }}
            </label>
            <input
              type="number"
              id="sessionNoStart"
              min="1"
              step="1"
              inputmode="numeric"
              :placeholder="$t('Default 1 if empty')"
              class="form-control shadow-none"
              v-model.number="v$.form.session_no_start.$model"
            >
            <div v-for="error of v$.form.session_no_start.$errors"
              class="d-block invalid-feedback"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading && hasPermission"
    >
      <ladda
        text="Save"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleOpenShift"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { POSRegisters } from '@/services/endpoint/pos.service';
import moment from "moment";
import { useScrollRestore } from '@/composables/useScrollRestore';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: {
    $lazy: true,
  },
  validations() {
    return validation(this.form)
  },
  props: {
    id: {
      type: Number,
      default: 0
    },
  },
  data() {
    let vm = this;
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      form: {
        starting_cash: '0.00',
        started_at: moment(Date.now()).format('HH:mm'),
        session_no_start: ''
      }
    }
  },
  components: {
    PlaceholderContent,
    Ladda,
    AccessDenied,
  },
  methods: {
    async fetchData() {
      await POSRegisters.getOpenShift(this.id).then(res => {
        this.hasPermission = true;
      }).catch(err => {
        this.hasPermission = false;
        this.loading = false;
			  throw err;
      });
    },
    setParamsBeforeSendOpenShift() {
      const allowed = ['starting_cash', 'started_at', 'session_no_start'];
      let params = _.pick(_.cloneDeep(this.form), allowed);
      params.started_at = moment(moment(Date.now()).format('YYYY-MM-DD') + ' ' + params.started_at).format('YYYY-MM-DD HH:mm:ss');
      if (params.session_no_start === '' || params.session_no_start === null || params.session_no_start === undefined) {
        delete params.session_no_start;
      } else {
        params.session_no_start = parseInt(params.session_no_start, 10);
      }
      return params;
    },
    async handleOpenShift() {
      this.loadingSave = true;
      this.v$.$touch();
      if(this.v$.$invalid) {
        this.loadingSave = false;
        return;
      }
      try {
        let self = this;
        let title = this.$t("Confirm Open Shift") + '?';
        let text = this.$t('Please verify the starting cash') + ' (' + this.$storeIndex.formatThaiCurrency(this.form.starting_cash) + ')' + this.$t(', register details, and the specified start time before proceeding.') + '?';
        let confirmButtonText = '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          text: text,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return POSRegisters.openShift(self.id, self.setParamsBeforeSendOpenShift())
              .then((response) => {
                self.$parent.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Open Shift Success!'));
                $(self.$refs.offcanvas).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else if (error?.response?.data?.code === "VALIDATION") {
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        }).then((res) => {
          this.loadingSave = false;
        });
      } catch (error) {
        this.loadingSave = false;
      }
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');

    $(this.$refs.offcanvas).on('shown.bs.offcanvas',async () => {
      this.loading = true;
      try {
        await this.fetchData();
        this.loading = false;
      } catch (err) {
      }
    });

    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasOpenShiftForm.open = false;
    });

    useScrollRestore(() => this.$refs.offcanvas?.querySelector('.offcanvas-body'));
  },
  computed: {
  }
}
</script>
